import { usePage, useSection } from "@tree-company/tree-query";
import { useEffect, useState } from "react";
import { UnprocessableEntityError } from "@tree-company/collect-js-client";
import * as Sentry from "@sentry/react";
import bbLogo from "../assets/images/logo_bb.png";
import {
  responseCollectorURL,
  survey,
  QUEUE_KEY,
  VOTE_YES,
  VOTE_NO,
  PROGRESS_KEY,
  reconcileQueue,
} from "../survey";
import { PROJECTS } from "../data/data";
import { fetchProjectQueue } from "../aggregator";
import translate from "../locales";
import Background from "./background";
import ResumeDialog from "./resume_dialog";
import { ArrowRightIcon, ItsmeIcon } from "./icons";

// A row exists the moment someone enters their RRN (the queue is saved before
// the first vote), so only treat a claimed session as a returning voter when it
// holds an actual vote or is completed.
function isReturningVoter(claimed) {
  if (claimed.completed) return true;
  return Object.entries(claimed.answers || {}).some(
    ([key, value]) =>
      key.startsWith("proj_") && (value === VOTE_YES || value === VOTE_NO),
  );
}

export default function IntroPage({ page, submitting, handleSave, children }) {
  const { title } = usePage(page);
  const {
    validating,
    value: rrnValue,
    maxLength,
    errors,
  } = useSection(page.sections[0]);
  const [error, setError] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const disableNext =
    validating ||
    (rrnValue || "").length !== maxLength ||
    (errors || []).length > 0;
  const page_description = translate("rrn.page_description");
  const itsmeAuthURL = `${responseCollectorURL}/auth/openid_connect?project_key=${import.meta.env.VITE_COLLECTOR_KEY}&survey_url=${window.location.origin}`;

  async function assignQueue() {
    const projectIds = PROJECTS.map((project) => project.id);
    const queue = await fetchProjectQueue(projectIds);
    page.store.setAnswer(QUEUE_KEY, queue ?? projectIds);
  }

  async function tryClaim() {
    let claimed = false;
    try {
      claimed = await page.store.claim();
    } catch {
      // We ignore any errors during claiming
      // If the server isn't responding, we'll still catch this when doing the manual submit later
    }

    if (claimed) {
      // The project list may have changed since the queue was generated: drop
      // removed projects, append new ones at the end of the deck.
      page.store.setAnswer(
        QUEUE_KEY,
        reconcileQueue(
          page.store.answers?.[QUEUE_KEY],
          PROJECTS.map((project) => project.id),
        ),
      );
      if (isReturningVoter(page.store)) {
        setShowResumePrompt(true);
      } else {
        survey.nextPage();
      }
    }
    return claimed;
  }

  async function getRrnFromURL() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("code")) return;

    const code = params.get("code");
    page.store.setAnswer("validationCode", code);

    // Clean the code out of the URL regardless of which path we take.
    params.delete("code");
    const newURL = new URL(window.location);
    newURL.search = params;
    window.history.replaceState({}, "", newURL.toString());

    const claimed = await tryClaim();
    if (claimed) return;

    try {
      await assignQueue();
      await page.store.save();
      survey.nextPage();
    } catch (error) {
      handleSaveError(error);
    }
  }

  function handleSaveError(error) {
    if (!(error instanceof UnprocessableEntityError)) {
      Sentry.captureException(error);
      setError(translate("rrn.errors.general"));
      return;
    }

    const reasons = error.details?.validation_code || [];

    if (reasons.includes("is not allowed")) {
      setError(translate("rrn.errors.not_allowed"));
    }
    if (reasons.includes("already used")) {
      Sentry.captureMessage(
        "Response Collector setting 'Reuse validation codes' switched off??",
      );
      setError(translate("rrn.errors.general"));
    }
  }

  // Manual RRN submit: look up an existing session before creating a new line.
  async function handleManualSubmit(event) {
    event.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    setError(null);

    const claimed = await tryClaim();
    if (claimed) return;
    try {
      await assignQueue();
      await handleSave(event);
    } catch (error) {
      handleSaveError(error);
    }

    setLoggingIn(false);
  }

  async function handleResume(restart) {
    setResuming(true);
    setError(null);

    // A completed session has nothing to resume mid-deck — both options replay
    // the projects from the start so the voter can adapt their choices.
    if (restart || page.store.completed) {
      page.store.setAnswer(PROGRESS_KEY, 0);
      page.store.setAnswer("completed", false);
    }

    page.store.save();
    survey.nextPage();
    setShowResumePrompt(false);
    setResuming(false);
  }

  useEffect(() => {
    getRrnFromURL();
  }, []);

  return (
    <div className="page page--intro">
      <Background />
      <main className="page__main">
        <img src={bbLogo} alt="" className="page__logo" />

        <div className="page__heading">
          <h1 className="page__title">{title}</h1>
          <p
            className="page__description"
            dangerouslySetInnerHTML={{ __html: page_description }}
          />
        </div>

        <div className="login-section">
          {children}

          <button
            className="button button--full-width"
            disabled={submitting || loggingIn || disableNext}
            onClick={handleManualSubmit}
          >
            {translate("rrn.submit")}
            <ArrowRightIcon className="button__icon" />
          </button>
        </div>

        <div className="divider">
          <div className="divider__line"></div>
          <span className="divider__text">{translate("rrn.divider")}</span>
          <div className="divider__line"></div>
        </div>

        <div className="login-section">
          <p className="login-section__label">{translate("rrn.itsme_label")}</p>
          <a
            href={itsmeAuthURL}
            className="button button--white button--full-width"
          >
            <ItsmeIcon className="button__image" />
            {translate("rrn.button")}
          </a>
          {error && <p className="error">{error}</p>}
        </div>
      </main>

      <ResumeDialog
        open={showResumePrompt}
        title={translate("rrn.resume.title")}
        detail={translate("rrn.resume.detail")}
        continueLabel={translate("rrn.resume.continue")}
        restartLabel={translate("rrn.resume.restart")}
        onContinue={() => handleResume(false)}
        onRestart={() => handleResume(true)}
        busy={resuming}
      />
    </div>
  );
}
